class HeroParallax extends HTMLElement {
  constructor() {
    super();
    this.parallaxElements = [];
    this.ticking = false;
    this.scrollHandler = this.handleScroll.bind(this);
    this.resizeHandler = this.handleResize.bind(this);
    this.observer = null;
  }

  connectedCallback() {
    this.setupParallax();
  }

  disconnectedCallback() {
    this.cleanup();
  }

  setupParallax() {
    // Check if parallax should be enabled
    if (!this.shouldEnableParallax()) return;

    // Get all parallax layers (only elements with data-parallax-layer="true")
    this.parallaxElements = this.querySelectorAll('[data-parallax-layer="true"]');
    
    if (this.parallaxElements.length === 0) return;

    // Set up intersection observer for performance
    this.setupIntersectionObserver();

    // Cache element data for performance
    this.cacheElementData();

    // Initial position update
    this.updateParallaxPositions();

    // Add scroll and resize listeners
    this.addEventListeners();
  }

  shouldEnableParallax() {
    // Check if parallax is enabled in settings
    const isEnabled = this.dataset.parallaxEnabled === 'true';
    
    // Check if we should disable on mobile
    const disableOnMobile = this.dataset.parallaxMobile === 'false';
    const isMobile = window.innerWidth < 750;
    
    return isEnabled && (!disableOnMobile || !isMobile);
  }

  setupIntersectionObserver() {
    const options = {
      rootMargin: '50% 0px',
      threshold: 0
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.isVisible = true;
        } else {
          this.isVisible = false;
        }
      });
    }, options);

    this.observer.observe(this);
  }

  cacheElementData() {
    this.parallaxData = Array.from(this.parallaxElements).map(element => {
      const speed = parseFloat(element.dataset.parallaxSpeed) || 0.5;
      const offset = parseFloat(element.dataset.parallaxOffset) || 0;
      
      return {
        element,
        speed,
        offset,
        height: element.offsetHeight
      };
    });

    // Cache viewport height
    this.viewportHeight = window.innerHeight;
    this.elementTop = this.offsetTop;
    this.elementHeight = this.offsetHeight;
  }

  addEventListeners() {
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    window.addEventListener('resize', this.resizeHandler);
  }

  handleScroll() {
    if (!this.isVisible) return;
    
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        this.updateParallaxPositions();
        this.ticking = false;
      });
      this.ticking = true;
    }
  }

  handleResize() {
    // Debounce resize
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      // Re-check if parallax should be enabled
      if (!this.shouldEnableParallax()) {
        this.resetParallax();
        return;
      }
      
      // Recache data
      this.cacheElementData();
      this.updateParallaxPositions();
    }, 250);
  }

  updateParallaxPositions() {
    const scrollY = window.pageYOffset;
    const windowHeight = window.innerHeight;
    const elementTop = this.getBoundingClientRect().top + scrollY;
    
    // Calculate the position of the element relative to the viewport
    const elementMiddle = elementTop + (this.elementHeight / 2);
    const windowMiddle = scrollY + (windowHeight / 2);
    const distanceFromCenter = windowMiddle - elementMiddle;
    
    this.parallaxData.forEach(({ element, speed, offset }) => {
      // Calculate transform amount
      const translateY = -(distanceFromCenter * speed) + offset;
      
      // Apply transform with will-change for performance
      element.style.willChange = 'transform';
      element.style.transform = `translate3d(0, ${translateY}px, 0)`;
    });
  }

  resetParallax() {
    this.parallaxElements.forEach(element => {
      element.style.transform = '';
      element.style.willChange = '';
    });
  }

  cleanup() {
    // Remove event listeners
    window.removeEventListener('scroll', this.scrollHandler);
    window.removeEventListener('resize', this.resizeHandler);
    
    // Disconnect observer
    if (this.observer) {
      this.observer.disconnect();
    }
    
    // Reset styles
    this.resetParallax();
    
    // Clear timeouts
    clearTimeout(this.resizeTimeout);
  }
}

// Define the custom element
if (!customElements.get('hero-parallax')) {
  customElements.define('hero-parallax', HeroParallax);
}